import { useAppDispatch, useAppSelector } from "app/hooks";

import { GuxButton } from "genesys-spark-components-react";
import { toGuxDisabled } from "util/componentUtil";

import strings from "./BottomBanner.strings";
import { useStrings } from "features/i18n/useStrings";

import {
    selectFormName,
    qualityFormActions,
    publishForm,
    saveForm,
    loadFormNames,
    EvaluationSettingsErrorPayload,
    AiScoringSettingsErrorPayload,
} from "features/qualityForm/qualityFormSlice";
import {
    selectAutomatedEvaluationFeatureEnabled,
    selectForm,
    selectFormType,
    selectIsAutomatedEvaluationForm,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";

import { copyFormSliceActions } from "./copyFormSlice";

import toastService from "services/toastService";
import embeddingService from "services/embeddingService";
import {
    addNewRelicPageActionForBottomBannerButtonClick,
    addNewRelicPageActionForSavingForm,
    setNewRelicCustomAttributeForCopyFeature,
} from "services/newRelic/features/nrUtilBottomBanner";
import FormType from "enums/FormType";
import {
    EvaluationFormResponse,
    SurveyForm,
} from "@genesys/cloud-frontend/models";
import { PayloadAction } from "@reduxjs/toolkit";
import FeatureToggle from "enums/FeatureToggle";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import {
    getFormsListRoutePath,
    getOldFormsListRoutePath,
    getFormDetailRoutePath,
    getOldFormDetailRoutePath,
    getAutomatedEvaluationFormDetailRoutePath,
} from "util/routeUtils";
// Check src/services/newRelic/newRelicService.js for more information about New Relic.

function hasEvaluationSettingsError(
    e: PayloadAction<
        | EvaluationFormResponse
        | SurveyForm
        | EvaluationSettingsErrorPayload
        | AiScoringSettingsErrorPayload
    >
): boolean {
    return e["payload"] && "settingsError" in e["payload"];
}

function hasAiScoringSettingsError(
    e: PayloadAction<
        | EvaluationFormResponse
        | SurveyForm
        | EvaluationSettingsErrorPayload
        | AiScoringSettingsErrorPayload
    >
): boolean {
    return e["payload"] && "aiScoringSettingsError" in e["payload"];
}

function setSessionStorageFromSettingsError(
    id: string,
    e: PayloadAction<
        EvaluationFormResponse | SurveyForm | EvaluationSettingsErrorPayload
    >
) {
    if (
        hasEvaluationSettingsError(e) &&
        e["payload"] &&
        "evaluationSettings" in e["payload"]
    ) {
        sessionStorage.setItem(
            "evaluationSettings",
            JSON.stringify({
                [id]: e["payload"]["evaluationSettings"],
            })
        );
    }
}

export enum BottomBannerButton {
    COPY = "copy",
    DISCARD_CHANGES = "discardChanges",
}

export function CopyFormButton({ disabled }: { disabled: boolean }) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const formName = useAppSelector(selectFormName) ?? "";

    return (
        <GuxButton
            className="copy-form"
            accent="secondary"
            disabled={toGuxDisabled(disabled)}
            aria-disabled={toGuxDisabled(disabled)}
            onClick={() => {
                addNewRelicPageActionForBottomBannerButtonClick(
                    BottomBannerButton.COPY
                );
                dispatch(
                    qualityFormActions.copyForm({
                        newFormName: t.formNameAfterCopy({ formName }),
                    })
                );
                dispatch(copyFormSliceActions.enableCopyingViewMode());
            }}
        >
            {t.FormButtons.CopyForm}
        </GuxButton>
    );
}

export function DiscardChangesButton() {
    const t = useStrings(strings);
    const formType = useAppSelector(selectFormType);
    const isAdminUIDecompEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.ADMIN_UI_DECOMPOSITION);
    });

    return (
        <GuxButton
            className="copy-form"
            accent="secondary"
            onClick={() => {
                addNewRelicPageActionForBottomBannerButtonClick(
                    BottomBannerButton.DISCARD_CHANGES
                );
                embeddingService.requestNavigation(
                    isAdminUIDecompEnabled
                        ? getFormsListRoutePath(formType)
                        : getOldFormsListRoutePath(formType)
                );
            }}
        >
            {t.FormButtons.DiscardChanges}
        </GuxButton>
    );
}

export function PublishFormButton({
    disabled,
    setInProgress,
    formType,
}: {
    disabled: boolean;
    setInProgress: React.Dispatch<React.SetStateAction<boolean>>;
    formType: FormType | undefined;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const isAdminUIDecompEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.ADMIN_UI_DECOMPOSITION);
    });

    return (
        <GuxButton
            className="publish-form"
            accent="secondary"
            disabled={toGuxDisabled(disabled)}
            aria-disabled={toGuxDisabled(disabled)}
            onClick={() => {
                setInProgress(true);
                dispatch(publishForm()).then((e) => {
                    if ("error" in e) {
                        toastService.notifyMinorAlert(
                            formType == FormType.EVALUATION
                                ? "composer.evaluations.editor.publishFail.title"
                                : "composer.surveys.editor.publishFail.title",
                            {
                                bodyMessageKey:
                                    formType == FormType.EVALUATION
                                        ? "composer.evaluations.editor.publishFail.message"
                                        : "composer.surveys.editor.publishFail.message",
                            }
                        );
                    } else {
                        if (hasEvaluationSettingsError(e)) {
                            toastService.notifyMajorAlert(
                                "composer.evaluations.editor.publishSaveSettingsFail.title",
                                {
                                    bodyMessageKey:
                                        "composer.evaluations.editor.publishSaveSettingsFail.message",
                                }
                            );
                            const id = e["payload"]?.id ?? "";
                            setSessionStorageFromSettingsError(id, e);
                            embeddingService.requestNavigation(
                                isAdminUIDecompEnabled
                                    ? getFormDetailRoutePath(formType, id)
                                    : getOldFormDetailRoutePath(formType, id)
                            );
                        } else {
                            toastService.notifyMessage(
                                formType == FormType.EVALUATION
                                    ? "composer.evaluations.editor.publishSuccess.title"
                                    : "composer.surveys.editor.publishSuccess.title",
                                {
                                    bodyMessageKey:
                                        formType == FormType.EVALUATION
                                            ? "composer.evaluations.editor.publishSuccess.message"
                                            : "composer.surveys.editor.publishSuccess.message",
                                    icon: "fa-check-circle",
                                },
                                false
                            );
                            embeddingService.requestNavigation(
                                isAdminUIDecompEnabled
                                    ? getFormsListRoutePath(formType)
                                    : getOldFormsListRoutePath(formType)
                            );
                        }
                    }
                    setInProgress(false);
                });
            }}
        >
            {t.FormButtons.Publish}
        </GuxButton>
    );
}

// might need to setShowAiScoringFailureModal
export function SaveButton({
    disabled,
    setInProgress,
    formType,
    isInCopyingViewMode,
    setShowAiScoringFailureModal,
    setRemoveAiScoringPermissionWithDiscardChanges,
    setWasNewForm,
}: {
    disabled: boolean;
    setInProgress: React.Dispatch<React.SetStateAction<boolean>>;
    formType: FormType | undefined;
    isInCopyingViewMode: boolean;
    setShowAiScoringFailureModal: React.Dispatch<React.SetStateAction<boolean>>;
    setRemoveAiScoringPermissionWithDiscardChanges: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    setWasNewForm: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const formId = useAppSelector((state) => selectForm(state)?.id);
    const isAdminUIDecompEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.ADMIN_UI_DECOMPOSITION);
    });
    const isAutomatedEvaluationCompletionFeatureEnabled = useAppSelector(
        selectAutomatedEvaluationFeatureEnabled
    );
    const isAutomatedEvaluationForm = useAppSelector(
        selectIsAutomatedEvaluationForm
    );

    return (
        <GuxButton
            className="save-form"
            accent="primary"
            disabled={toGuxDisabled(disabled)}
            aria-disabled={toGuxDisabled(disabled)}
            onClick={() => {
                setInProgress(true);

                setNewRelicCustomAttributeForCopyFeature(isInCopyingViewMode);
                dispatch(saveForm()).then((e) => {
                    if ("error" in e) {
                        toastService.notifyMinorAlert(
                            formType == FormType.EVALUATION
                                ? "composer.evaluations.editor.saveFail.title"
                                : "composer.surveys.editor.saveFail.title",
                            {
                                bodyMessageKey:
                                    formType == FormType.EVALUATION
                                        ? "composer.evaluations.editor.saveFail.message"
                                        : "composer.surveys.editor.saveFail.message",
                            }
                        );

                        addNewRelicPageActionForSavingForm(false);

                        if (formType !== undefined) {
                            dispatch(
                                loadFormNames(
                                    formType === FormType.EVALUATION
                                        ? FormType.EVALUATION
                                        : FormType.SURVEY
                                )
                            );
                        }
                    } else {
                        const isNewForm = formId === "new";
                        if (hasEvaluationSettingsError(e)) {
                            toastService.notifyMajorAlert(
                                isNewForm
                                    ? "composer.evaluations.editor.createSaveSettingsFail.title"
                                    : "composer.evaluations.editor.saveSettingsFail.title",
                                {
                                    bodyMessageKey: isNewForm
                                        ? "composer.evaluations.editor.createSaveSettingsFail.message"
                                        : "composer.evaluations.editor.saveSettingsFail.message",
                                }
                            );
                        } else if (hasAiScoringSettingsError(e)) {
                            // notify for saving the form successfully
                            toastService.notifyMessage(
                                "composer.evaluations.editor.saveSuccess.title",
                                {
                                    bodyMessageKey:
                                        "composer.evaluations.editor.saveSuccess.message",
                                    icon: "fa-check-circle",
                                },
                                false
                            );
                            setShowAiScoringFailureModal(true);
                            if (
                                e?.payload?.[
                                    "isAiScoringEditPermissionError"
                                ] === true
                            ) {
                                setRemoveAiScoringPermissionWithDiscardChanges(
                                    true
                                );
                            }
                        } else {
                            toastService.notifyMessage(
                                formType == FormType.EVALUATION
                                    ? "composer.evaluations.editor.saveSuccess.title"
                                    : "composer.surveys.editor.saveSuccess.title",
                                {
                                    bodyMessageKey:
                                        formType == FormType.EVALUATION
                                            ? "composer.evaluations.editor.saveSuccess.message"
                                            : "composer.surveys.editor.saveSuccess.message",
                                    icon: "fa-check-circle",
                                },
                                false
                            );
                        }

                        addNewRelicPageActionForSavingForm(true);

                        const form =
                            "evaluationForm" in e["payload"]
                                ? e["payload"].evaluationForm
                                : e["payload"];
                        const id = form?.id ?? "";
                        setWasNewForm(isNewForm);
                        if (
                            isNewForm &&
                            !hasEvaluationSettingsError(e) &&
                            !hasAiScoringSettingsError(e)
                        ) {
                            let formRoute = getFormDetailRoutePath(
                                formType,
                                id
                            );

                            if (
                                isAutomatedEvaluationCompletionFeatureEnabled &&
                                isAutomatedEvaluationForm
                            ) {
                                formRoute =
                                    getAutomatedEvaluationFormDetailRoutePath(
                                        formType,
                                        id
                                    );
                            }

                            embeddingService.requestNavigation(
                                isAdminUIDecompEnabled
                                    ? formRoute
                                    : getOldFormDetailRoutePath(formType, id)
                            );
                        }
                    }
                    setInProgress(false);
                });
            }}
        >
            {t.FormButtons.Save}
        </GuxButton>
    );
}

export function DisableFormButton({
    setShowConfirmationPopUp,
}: {
    setShowConfirmationPopUp: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const t = useStrings(strings);
    const isFormDisabled = useAppSelector(
        (state) => selectForm(state)?.disabled ?? false
    );

    return (
        <GuxButton
            className="publish-form"
            accent="primary"
            disabled={toGuxDisabled(isFormDisabled)}
            aria-disabled={toGuxDisabled(isFormDisabled)}
            onClick={() => {
                setShowConfirmationPopUp(true);
            }}
        >
            {t.FormButtons.Disable}
        </GuxButton>
    );
}
